import { useSelector } from "react-redux";
import { getLoanPurpose, getNhDealApplicationId, getCurrentUserId } from "features/authSlice";
import { useGetloanOfficerQuery } from "pages/application/applicationService";
import { SurveyType, useGetSurveyDetailsQuery } from "pages/survey/surveyService";
import Modal from "components/modal/Modal";
import "./nps-rating-modal.scss";
import x_mark from "assets/images/x.svg";
import npsRatingLikely from "assets/images/nps-rating-likely.svg";
import npsRatingUnlikely from "assets/images/nps-rating-unlikely.svg";
import npsRatingVeryLikely from "assets/images/nps-rating-verylikely.svg";
import {
  NPS_MODAL_HEADER,
  NPS_MODAL_VERY_LIKELY_LABEL,
  NPS_MODAL_SUBTITLE,
  NPS_MODAL_UNLIKELY_LABEL,
  NPS_MODAL_LIKELY_LABEL,
  NPS_MODAL_UNLIKELY_DESCRIPTION,
  NPS_MODAL_LIKELY_DESCRIPTION,
  NPS_MODAL_VERY_LIKELY_DESCRIPTION,
  NPS_MODAL_CHARACTER_LIMIT,
  NPS_MODAL_COMMENT_PLACEHOLDER,
  NPS_SCROLL_TIMEOUT,
  NPS_MOUNT_TIMEOUT,
  NPS_INITIAL_SCROLL_OFFSET,
  NPS_FEEDBACK_SUBMISSION_SUCCESS_HEADER,
} from "utilities/Constant";
import { useState, useRef, useEffect } from "react";
import { Button } from "components/ui/button/Button";
import useDesktopChecker from "hooks/useDesktopChecker";
import { trackFSNpsDisplayed, trackFSNpsCompleted, trackFSNpsClosed } from "utilities/full-story";

interface RatingSelection {
  rating: number;
  smileyImageSrc: string;
  ratingLabel: string;
  ratingDescription?: string;
  comment?: string;
}

interface NpsRatingModalProps {
  readonly onCloseModal?: () => void;
  readonly onDismissModal?: () => void;
  readonly onSubmit?: (ratingData: RatingSelection | null) => void;
}

export default function NpsRatingModal({ onCloseModal, onDismissModal, onSubmit }: NpsRatingModalProps) {
  const ratings = Array.from({ length: 11 }, (_, i) => i);
  const [selectedRatingData, setSelectedRatingData] = useState<RatingSelection | null>(null);
  const ratingsContainerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useDesktopChecker();
  const [hasScrolled, setHasScrolled] = useState(false);
  const isInitialMount = useRef(true);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const loanPurpose = useSelector(getLoanPurpose);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const { data: loanOfficerDetails } = useGetloanOfficerQuery(nhDealApplicationId, { skip: !nhDealApplicationId });
  const userId = useSelector(getCurrentUserId);
  const { data: surveyDetails } = useGetSurveyDetailsQuery({ userId, nhDealApplicationId, surveyType: SurveyType.NPS }, { skip: !userId || !nhDealApplicationId });

  const firstDomainName = (loanOfficerDetails?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
  let salesChannel: string | undefined;
  if (firstDomainName === "retail") {
    salesChannel = "OLA";
  } else if (firstDomainName === "cd") {
    salesChannel = "ILA";
  }

  const handleSubmitFeedback = () => {
    if (!selectedRatingData) return;

    setIsFeedbackSubmitted(true);
    trackFSNpsCompleted({ isComplete: true });
    trackFSNpsClosed({
      loanPurpose,
      salesChannel,
      npsScore: selectedRatingData.rating,
      npsFeedback: selectedRatingData.comment ?? "",
    });
    if (onSubmit) {
      onSubmit(selectedRatingData);
    }
  };

  const getRatingData = (rating: number): RatingSelection => {
    if (rating <= 6) {
      return {
        rating,
        smileyImageSrc: npsRatingUnlikely,
        ratingLabel: NPS_MODAL_UNLIKELY_LABEL,
        ratingDescription: NPS_MODAL_UNLIKELY_DESCRIPTION,
        comment: "",
      };
    } else if (rating <= 8) {
      return {
        rating,
        smileyImageSrc: npsRatingLikely,
        ratingLabel: NPS_MODAL_LIKELY_LABEL,
        ratingDescription: NPS_MODAL_LIKELY_DESCRIPTION,
        comment: "",
      };
    } else {
      return {
        rating,
        smileyImageSrc: npsRatingVeryLikely,
        ratingLabel: NPS_MODAL_VERY_LIKELY_LABEL,
        ratingDescription: NPS_MODAL_VERY_LIKELY_DESCRIPTION,
        comment: "",
      };
    }
  };

  const getRatingSectionCenter = (): number => {
    if (!ratingsContainerRef.current) return 0;
    const container = ratingsContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    return containerRect.left + containerRect.width / 2;
  };

  const setComment = (comment: string) => {
    setSelectedRatingData((prev) => (prev ? { ...prev, comment } : null));
  };

  const handleScroll = () => {
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      if (!ratingsContainerRef.current || isDesktop || isInitialMount.current) return;

      if (!hasScrolled) {
        setHasScrolled(true);
      }

      // Find rating closest to the center
      const centerX = getRatingSectionCenter();
      const container = ratingsContainerRef.current;
      const ratingElements = container.querySelectorAll(".feedback-modal__rating-value");
      if (ratingElements.length === 0) return;
      let closestRating = 0;
      let minDistance = Infinity;
      ratingElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementCenterX = rect.left + rect.width / 2;
        const distance = Math.abs(centerX - elementCenterX);
        if (distance < minDistance) {
          minDistance = distance;
          closestRating = index;
        }
      });
      setSelectedRatingData(getRatingData(closestRating));
    }, NPS_SCROLL_TIMEOUT);
  };

  const handleRatingClick = (rating: number) => {
    if (isDesktop) {
      setSelectedRatingData(getRatingData(rating));
    } else {
      // Move the clicked rating to the center (for mobile devices)
      if (!ratingsContainerRef.current) return;
      const container = ratingsContainerRef.current;
      const centerX = getRatingSectionCenter();
      const ratingElements = container.querySelectorAll(".feedback-modal__rating-value");
      const clickedElement = Array.from(ratingElements).find((el) => Number((el as HTMLElement).dataset.rating) === rating);
      if (!clickedElement) return;
      const rect = clickedElement.getBoundingClientRect();
      const elementCenterX = rect.left + rect.width / 2;

      // Clicked rating is already centered
      if (hasScrolled && Math.abs(centerX - elementCenterX) < 1) return;
      else if (!hasScrolled && Math.abs(centerX - elementCenterX) < 1) {
        setSelectedRatingData(getRatingData(rating));
        setHasScrolled(true);
        return;
      }

      const scrollOffset = elementCenterX - centerX;
      container.scrollBy({
        left: scrollOffset,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const displayedCount = surveyDetails?.displayedCount;
    const count = typeof displayedCount === "number" ? displayedCount + 1 : 1;

    trackFSNpsDisplayed({
      count,
      loanPurpose,
      salesChannel,
    });
  }, [salesChannel]);

  const handleDismissModal = () => {
    trackFSNpsCompleted({ isComplete: false });

    trackFSNpsClosed({
      loanPurpose,
      salesChannel,
      npsScore: selectedRatingData?.rating ?? null,
      npsFeedback: selectedRatingData?.comment ?? "",
    });

    if (onDismissModal) {
      onDismissModal();
    }
  };

  useEffect(() => {
    if (!isDesktop && ratingsContainerRef.current) {
      const container = ratingsContainerRef.current;
      const scrollTimeoutId = setTimeout(() => {
        container.scrollTo({
          left: NPS_INITIAL_SCROLL_OFFSET,
          behavior: "smooth",
        });
      }, NPS_SCROLL_TIMEOUT);

      const mountTimeoutId = setTimeout(() => {
        isInitialMount.current = false;
      }, NPS_MOUNT_TIMEOUT);

      return () => {
        clearTimeout(scrollTimeoutId);
        clearTimeout(mountTimeoutId);
        clearTimeout(scrollTimeout.current);
      };
    }
  }, [isDesktop]);

  return (
    <div className="feedback-modal">
      <Modal
        className="modal"
        customHeader={
          <div className="feedback-modal__header">
            {isFeedbackSubmitted ? <span></span> : <h5 className="feedback-modal__heading">{NPS_MODAL_HEADER}</h5>}
            <button type="button" className="feedback-modal__close-icon" aria-label="Close" onClick={handleDismissModal}><img src={x_mark} alt="" /></button>
          </div>
        }
        customBody={
          <div className="feedback-modal__body">
            {isFeedbackSubmitted ? (
              <>
                <div className="feedback-modal__success-header">{NPS_FEEDBACK_SUBMISSION_SUCCESS_HEADER}</div>
                <div className="feedback-modal__footer">
                  <Button title="Close" onClick={onCloseModal} />
                </div>
              </>
            ) : (
              <>
                <div className="feedback-modal__title-section">
                  <h5 className="feedback-modal__title">{NPS_MODAL_SUBTITLE}</h5>
                </div>
                <div className="feedback-modal__ratings-wrapper">
                  {!isDesktop && hasScrolled && <div className="feedback-modal__center-ring" />}
                  <div className="feedback-modal__ratings-section" ref={ratingsContainerRef} onScroll={handleScroll}>
                    {ratings.map((rating) => (
                      <button
                        type="button"
                        key={rating}
                        data-rating={rating}
                        className={`feedback-modal__rating-value ${
                          selectedRatingData?.rating === rating ? "feedback-modal__rating-value--selected" : ""
                        }`}
                        aria-pressed={selectedRatingData?.rating === rating}
                        onClick={() => handleRatingClick(rating)}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="feedback-modal__rating-reactionsection">
                  <h5 className="feedback-modal__reactionlabel">{NPS_MODAL_UNLIKELY_LABEL}</h5>
                  <h5 className="feedback-modal__reactionlabel">{NPS_MODAL_VERY_LIKELY_LABEL}</h5>
                </div>
                <div className="feedback-modal__smiley-section">
                  {selectedRatingData && (
                    <>
                      <img className="feedback-modal__smiley-image" src={selectedRatingData.smileyImageSrc} alt="NPS Rating Smiley" />
                      <div className="feedback-modal__rating-label">{selectedRatingData.ratingLabel}</div>
                    </>
                  )}
                </div>

                {selectedRatingData && (
                  <div className="feedback-modal__comment-section">
                    <h5>{selectedRatingData?.ratingDescription}</h5>
                    <textarea
                      placeholder={NPS_MODAL_COMMENT_PLACEHOLDER}
                      onChange={(e) => setComment(e.target.value)}
                      maxLength={NPS_MODAL_CHARACTER_LIMIT}
                      value={selectedRatingData?.comment}
                    />
                    <span>
                      {selectedRatingData?.comment?.length || 0}/{NPS_MODAL_CHARACTER_LIMIT}
                    </span>
                  </div>
                )}

                <div className="feedback-modal__footer">
                  <Button title="Submit" disabled={!selectedRatingData} onClick={handleSubmitFeedback} />
                </div>
              </>
            )}
          </div>
        }
      />
    </div>
  );
}
